'use client';

import React, { useState, useEffect, useRef } from 'react';
import Icon from '@/components/ui/AppIcon';

interface GenerationProgressModalProps {
  prompt: string;
  modelName: string;
  modelId: string;
  onComplete: (gameCode: string) => void;
}

const STEPS = [
  { id: 'step-analyze', label: 'A analisar o prompt', duration: 5000 },
  { id: 'step-design', label: 'A desenhar a arquitetura do jogo', duration: 8000 },
  { id: 'step-generate', label: 'A gerar o código do jogo', duration: 0 }, // driven by API
  { id: 'step-assets', label: 'A criar os assets visuais', duration: 7000 },
  { id: 'step-compile', label: 'A compilar e otimizar', duration: 5000 },
  { id: 'step-test', label: 'A testar o jogo', duration: 4000 },
];

// Progress ranges per step: [start%, end%]
const STEP_PROGRESS: [number, number][] = [
  [0, 15],   // analyze
  [15, 30],  // design
  [30, 50],  // generate (held at 30 until API returns, then jumps to 50)
  [50, 65],  // assets
  [65, 80],  // compile
  [80, 95],  // test
];

export default function GenerationProgressModal({
  prompt,
  modelName,
  modelId,
  onComplete,
}: GenerationProgressModalProps) {
  const [currentStep, setCurrentStep] = useState(0);
  const [overallProgress, setOverallProgress] = useState(0);
  const [elapsed, setElapsed] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const hasStarted = useRef(false);

  useEffect(() => {
    if (hasStarted.current) return;
    hasStarted.current = true;

    let cancelled = false;
    let gameCode = '';

    // Smoothly animate progress from `from` to `to` over `durationMs`
    const animateProgress = (from: number, to: number, durationMs: number): Promise<void> => {
      return new Promise((resolve) => {
        if (durationMs <= 0) {
          setOverallProgress(to);
          resolve();
          return;
        }
        const startTime = Date.now();
        const tick = () => {
          if (cancelled) { resolve(); return; }
          const elapsed = Date.now() - startTime;
          const t = Math.min(elapsed / durationMs, 1);
          // ease-out cubic
          const eased = 1 - Math.pow(1 - t, 3);
          const current = Math.round(from + (to - from) * eased);
          setOverallProgress(current);
          if (t < 1) {
            requestAnimationFrame(tick);
          } else {
            resolve();
          }
        };
        requestAnimationFrame(tick);
      });
    };

    const run = async () => {
      // Step 0: analyze
      setCurrentStep(0);
      setOverallProgress(0);
      await animateProgress(0, 15, STEPS[0].duration);
      if (cancelled) return;

      // Step 1: design
      setCurrentStep(1);
      await animateProgress(15, 30, STEPS[1].duration);
      if (cancelled) return;

      // Step 2: generate — call API while showing slow progress crawl
      setCurrentStep(2);
      // Start a slow crawl from 30 → 48 while API runs (up to 90s)
      let apiDone = false;
      let apiError: string | null = null;

      const crawlPromise = (async () => {
        const crawlStart = Date.now();
        const maxCrawl = 90000;
        while (!apiDone && !cancelled) {
          const t = Math.min((Date.now() - crawlStart) / maxCrawl, 1);
          const eased = 1 - Math.pow(1 - t, 2);
          const crawlProgress = Math.round(30 + 18 * eased); // 30 → 48
          setOverallProgress(crawlProgress);
          await delay(200);
        }
      })();

      try {
        const res = await fetch('/api/generate-game', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ prompt, modelId }),
        });
        if (!res.ok) {
          const errData = await res.json().catch(() => ({}));
          throw new Error(errData?.error ?? `HTTP ${res.status}`);
        }
        const data = await res.json();
        if (data.error) throw new Error(data.error);
        gameCode = data.gameCode ?? '';
      } catch (e: any) {
        console.error('Generation error:', e);
        apiError = e.message ?? 'Erro desconhecido';
      } finally {
        apiDone = true;
      }

      await crawlPromise;
      if (cancelled) return;

      if (apiError) {
        setError(apiError);
        return;
      }

      // Step 3: assets
      setCurrentStep(3);
      await animateProgress(50, 65, STEPS[3].duration);
      if (cancelled) return;

      // Step 4: compile
      setCurrentStep(4);
      await animateProgress(65, 80, STEPS[4].duration);
      if (cancelled) return;

      // Step 5: test
      setCurrentStep(5);
      await animateProgress(80, 95, STEPS[5].duration);
      if (cancelled) return;

      // Done
      setCurrentStep(6);
      await animateProgress(95, 100, 600);
      await delay(400);
      if (!cancelled) onComplete(gameCode);
    };

    run();

    const elapsedInterval = setInterval(() => {
      setElapsed((prev) => prev + 1);
    }, 1000);

    return () => {
      cancelled = true;
      clearInterval(elapsedInterval);
    };
  }, [prompt, modelId, onComplete]);

  if (error) {
    return (
      <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
        <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" />
        <div className="relative w-full max-w-lg bg-card border border-border rounded-2xl p-8 shadow-2xl text-center">
          <div className="w-16 h-16 rounded-full bg-danger/15 flex items-center justify-center mx-auto mb-4">
            <Icon name="ExclamationCircleIcon" size={32} className="text-danger" />
          </div>
          <h2 className="text-xl font-bold text-foreground mb-2">Erro na geração</h2>
          <p className="text-sm text-muted-foreground mb-6">{error}</p>
          <button
            onClick={() => window.location.reload()}
            className="px-6 py-2.5 rounded-xl gradient-primary text-white text-sm font-semibold hover:opacity-90 transition-opacity"
          >
            Tentar novamente
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" />
      <div className="relative w-full max-w-lg bg-card border border-border rounded-2xl p-8 shadow-2xl animate-scale-in">
        {/* Animated icon */}
        <div className="flex justify-center mb-6">
          <div className="relative w-20 h-20">
            <div className="absolute inset-0 rounded-full gradient-primary opacity-20 animate-ping" />
            <div className="relative w-20 h-20 rounded-full gradient-primary flex items-center justify-center glow-primary">
              <Icon name="SparklesIcon" size={32} className="text-white animate-pulse" variant="solid" />
            </div>
          </div>
        </div>

        <h2 className="text-xl font-bold text-foreground text-center mb-2">
          A criar o teu jogo...
        </h2>
        <p className="text-sm text-muted-foreground text-center mb-1">
          Usando <span className="text-primary font-medium">{modelName}</span>
        </p>
        <p className="text-xs text-muted-foreground text-center mb-6 italic max-w-sm mx-auto truncate">
          "{prompt.substring(0, 80)}{prompt.length > 80 ? '...' : ''}"
        </p>

        {/* Overall progress */}
        <div className="mb-6">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs text-muted-foreground">Progresso geral</span>
            <span className="text-xs font-mono font-semibold text-foreground tabular-nums">{overallProgress}%</span>
          </div>
          <div className="h-2 bg-muted rounded-full overflow-hidden">
            <div
              className="h-full rounded-full gradient-primary transition-none"
              style={{ width: `${overallProgress}%` }}
            />
          </div>
        </div>

        {/* Steps */}
        <div className="space-y-2 mb-6">
          {STEPS.map((step, index) => {
            const isDone = index < currentStep;
            const isActive = index === currentStep;
            return (
              <div key={step.id} className="flex items-center gap-3">
                <div className={`
                  w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 transition-all duration-300
                  ${isDone ? 'bg-success' : isActive ? 'bg-primary' : 'bg-muted'}
                `}>
                  {isDone ? (
                    <Icon name="CheckIcon" size={11} className="text-white" />
                  ) : isActive ? (
                    <Icon name="ArrowPathIcon" size={11} className="text-white animate-spin" />
                  ) : (
                    <div className="w-2 h-2 rounded-full bg-muted-foreground/40" />
                  )}
                </div>
                <span className={`text-xs transition-colors duration-200 ${
                  isDone ? 'text-muted-foreground line-through' :
                  isActive ? 'text-foreground font-medium' : 'text-muted-foreground/60'
                }`}>
                  {step.label}
                </span>
              </div>
            );
          })}
        </div>

        {/* Elapsed time */}
        <div className="text-center">
          <p className="text-xs text-muted-foreground">
            Tempo decorrido:{' '}
            <span className="font-mono text-foreground tabular-nums">{elapsed}s</span>
          </p>
          <p className="text-xs text-muted-foreground/60 mt-1">
            A geração pode demorar até 2 minutos para garantir a melhor qualidade
          </p>
        </div>
      </div>
    </div>
  );
}

function delay(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}